import React from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import App from './App';
import './index.css';
import { enablePosthogHeavyFeatures, initPosthog } from './services/posthogClient';

const isDevelopment = process.env.NODE_ENV === 'development';

const isLandingPath = (pathname) => pathname === '/';
const isLandingAtBoot = typeof window !== 'undefined' && isLandingPath(window.location.pathname);

const schedulePosthogHeavyEnableAfterIdle = () => {
	if (typeof window === 'undefined') {
		return;
	}

	if ('requestIdleCallback' in window) {
		window.requestIdleCallback(() => enablePosthogHeavyFeatures(), { timeout: 3000 });
	} else {
		if (document.readyState === 'complete') {
			setTimeout(enablePosthogHeavyFeatures, 0);
		} else {
			window.addEventListener('load', () => setTimeout(enablePosthogHeavyFeatures, 0), { once: true });
		}
	}
};

const setupLandingPosthogTriggers = () => {
	if (typeof window === 'undefined') {
		return;
	}

	let triggered = false;
	const events = ['pointerdown', 'keydown', 'touchstart', 'scroll'];

	const cleanup = () => {
		events.forEach((eventName) => window.removeEventListener(eventName, trigger));
		window.clearTimeout(fallbackTimer);
	};

	const trigger = () => {
		if (triggered) {
			return;
		}
		triggered = true;
		cleanup();
		schedulePosthogHeavyEnableAfterIdle();
	};

	events.forEach((eventName) => {
		window.addEventListener(eventName, trigger, { once: true, passive: true });
	});

	const fallbackTimer = window.setTimeout(trigger, 15000);
};

if (typeof window === 'undefined') {
	initPosthog();
} else {
	// Keep vitals/pageview collection active immediately on all routes.
	initPosthog({ disableFeatureFlagsOnFirstLoad: isLandingAtBoot });

	if (isDevelopment || !isLandingPath(window.location.pathname)) {
		schedulePosthogHeavyEnableAfterIdle();
	} else {
		setupLandingPosthogTriggers();
	}
}

const rootElement = document.getElementById('root');

// Re-enable StrictMode by uncommenting this block:
// const app = (
// 	<React.StrictMode>
// 		<App />
// 	</React.StrictMode>
// );
const app = <App />;

if (rootElement && rootElement.hasChildNodes()) {
	hydrateRoot(rootElement, app);
} else if (rootElement) {
	createRoot(rootElement).render(app);
}

if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
	window.addEventListener('load', () => {
		navigator.serviceWorker.register('/service-worker.js').catch((error) => {
			console.error('Service worker registration failed:', error);
		});
	});
}

if (!isDevelopment) {
	// Silence non-critical logs in production to keep the console clean
	console.log = () => { };
	console.debug = () => { };
	console.info = () => { };
}
